import { useReviewsQuery } from '../api/queries';
import { makeHref, renderStars } from '../lib/format';
import { copyToClipboard, downloadImage } from '../lib/clipboard';
import { showToast } from '../components/Toast';
import type { Franchisee, Review } from '../types';

type Props = { franchisee: Franchisee | null };

export default function WebsiteTab({ franchisee }: Props) {
  if (!franchisee) {
    return (
      <div className="tab-placeholder">
        <h3>Live Website &amp; Reviews</h3>
        <p>Select a franchisee to view their website preview, reviews, and Google Business Profile.</p>
      </div>
    );
  }

  const { site_url, gmb_link, marketing_name, city, state } = franchisee;

  return (
    <>
      <div className="preview-headers">
        <div className="section-header">
          <h2>Website Preview</h2>
          <span className="section-badge badge-mobile">Mobile View</span>
        </div>
        <div className="section-header">
          <h2>Google Business Profile and Reviews Info</h2>
        </div>
      </div>

      <div className="preview-grid">
        <MobilePanel siteUrl={site_url ?? null} name={marketing_name ?? ''} />
        <GmbMapCard
          gmbLink={gmb_link ?? null}
          name={marketing_name ?? ''}
          city={city ?? null}
          state={state ?? null}
        />
        <ReviewsPanel
          gmbLink={gmb_link ?? null}
          name={marketing_name ?? ''}
          af={franchisee.af_number}
        />
      </div>

      <div className="section-header">
        <h2>Website Preview</h2>
        <span className="section-badge badge-desktop">Desktop View</span>
      </div>

      <DesktopView siteUrl={site_url ?? null} name={marketing_name ?? ''} />
    </>
  );
}

function MobilePanel({ siteUrl, name }: { siteUrl: string | null; name: string }) {
  if (!siteUrl) {
    return (
      <div className="preview-website">
        <div className="pw-header">
          <span className="pw-title">
            <GlobeIcon /> Mobile View
          </span>
        </div>
        <div className="pw-body">
          <div className="pw-no-url">
            <NoUrlIcon />
            <p>No website URL configured</p>
          </div>
        </div>
      </div>
    );
  }
  const href = makeHref(siteUrl);
  return (
    <div className="preview-website">
      <div className="pw-header">
        <span className="pw-title">
          <GlobeIcon /> {name} — Mobile View
        </span>
        <a href={href} target="_blank" rel="noopener noreferrer">
          Open in new tab ↗
        </a>
      </div>
      <div className="pw-body">
        <iframe src={href} title={`${name} (mobile preview)`} loading="lazy" />
      </div>
    </div>
  );
}

function StarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function GmbMapCard({
  gmbLink,
  name,
  city,
  state,
}: {
  gmbLink: string | null;
  name: string;
  city: string | null;
  state: string | null;
}) {
  if (!gmbLink) {
    return (
      <div className="plc-no-url">
        <NoUrlIcon />
        <p>No GMB profile configured</p>
      </div>
    );
  }
  const reviewsHref = makeHref(gmbLink);
  // Anchor the map to the franchisee's actual locale. Without city/state, a
  // bare name search drifts to same-named places elsewhere — e.g. "Amada North
  // Shore Senior Care" geocodes to the North Shore region of Louisiana instead
  // of the Skokie, IL office (AF-167). City + state pins it to the real listing.
  const locale = [city, state].filter(Boolean).join(' ');
  const searchQuery = encodeURIComponent(
    `${name} Senior Care${locale ? ` ${locale}` : ''}`,
  );
  const mapSrc = `https://www.google.com/maps?q=${searchQuery}&output=embed`;
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&data=${encodeURIComponent(reviewsHref)}`;

  const qrFilename = `${(name || 'reviews').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '') || 'reviews'}-qr.png`;

  async function handleCopyReviews() {
    const ok = await copyToClipboard(reviewsHref);
    showToast(ok ? 'Copied to clipboard!' : 'Could not copy link', {
      variant: ok ? 'success' : 'error',
    });
  }

  async function handleDownloadQr() {
    try {
      await downloadImage(qrSrc, qrFilename);
      showToast('QR code downloaded', { variant: 'success' });
    } catch (err) {
      showToast(`Download failed: ${(err as Error).message}`, { variant: 'error' });
    }
  }

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${searchQuery}`;

  return (
    <div className="gmb-map-card">
      <div className="pw-header">
        <span className="pw-title">
          <MapPinIcon /> {name} — Google Map
        </span>
        <a href={mapsHref} target="_blank" rel="noopener noreferrer">
          Open in new tab ↗
        </a>
      </div>
      <iframe src={mapSrc} title="GMB map" loading="lazy" allowFullScreen />
      <div className="gmb-bottom-bar">
        <button
          type="button"
          className="gmb-qr-thumb"
          title="Click to download Reviews QR Code"
          onClick={handleDownloadQr}
        >
          <img src={qrSrc} alt="QR" />
        </button>
        <div className="gmb-bar-actions">
          <button type="button" className="gmb-reviews-btn" onClick={handleCopyReviews}>
            <span className="gmb-reviews-stars">★★★★★</span>
            Your Reviews Link
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
          <button type="button" className="gmb-dl-btn" onClick={handleDownloadQr}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download QR Code
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewsPanel({
  gmbLink,
  name,
  af,
}: {
  gmbLink: string | null;
  name: string;
  af: string;
}) {
  void name;
  const { data, isLoading, error } = useReviewsQuery(af);
  const reviewsHref = gmbLink ? makeHref(gmbLink) : '';

  return (
    <div className="reviews-panel">
      <div className="pw-header reviews-header">
        <span className="pw-title">
          <StarIcon /> Reviews
        </span>
        {reviewsHref && (
          <a href={reviewsHref} target="_blank" rel="noopener noreferrer">
            Open in Google ↗
          </a>
        )}
      </div>
      <div className="reviews-body" id="reviewsBody">
        {isLoading && (
          <div className="reviews-loading">
            <div className="spinner" />
            <div>Loading reviews...</div>
          </div>
        )}
        {error && (
          <div className="reviews-unavailable">
            <p>Unable to load reviews. Please try again later.</p>
          </div>
        )}
        {data && <ReviewsContent data={data} />}
      </div>
    </div>
  );
}

function ReviewsContent({
  data,
}: {
  data: { rating?: number; userRatingCount?: number; reviews?: Review[] };
}) {
  if (!data.reviews || data.reviews.length === 0) {
    return (
      <div className="reviews-unavailable">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#f59e0b"
          strokeWidth="1.5"
        >
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        <p>No reviews found for this location.</p>
      </div>
    );
  }
  const ratingNum = data.rating ?? 0;
  const totalReviews = data.userRatingCount ?? 0;
  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          padding: '0 0 20px',
          borderBottom: '2px solid #f0f2f5',
          marginBottom: 8,
        }}
      >
        <div style={{ fontSize: 48, fontWeight: 800, color: '#1a3a5c' }}>{ratingNum.toFixed(1)}</div>
        <div>
          <div className="review-stars" style={{ fontSize: 22 }}>
            {renderStars(ratingNum)}
          </div>
          <div style={{ fontSize: 13, color: '#5a6a7a', marginTop: 2 }}>
            {totalReviews.toLocaleString()} reviews
          </div>
        </div>
      </div>
      {data.reviews.map((r, i) => (
        <ReviewCard key={i} review={r} />
      ))}
    </>
  );
}

function ReviewCard({ review: r }: { review: Review }) {
  const author = r.authorName || 'Anonymous';
  const initial = author.charAt(0).toUpperCase();
  return (
    <div className="review-card">
      <div className="review-top">
        {r.photoUri ? (
          <img
            src={r.photoUri}
            alt=""
            style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
          />
        ) : (
          <div className="review-avatar">{initial}</div>
        )}
        <div className="review-meta">
          <div className="review-author">{author}</div>
          <div className="review-date">{r.relativeTime ?? ''}</div>
        </div>
      </div>
      <div className="review-stars">{renderStars(r.rating ?? 0)}</div>
      {r.text && <div className="review-text">{r.text}</div>}
    </div>
  );
}

function DesktopView({ siteUrl, name }: { siteUrl: string | null; name: string }) {
  if (!siteUrl) {
    return (
      <div className="desktop-view">
        <div className="dv-header">
          <span className="dv-title">Desktop View</span>
        </div>
        <div className="dv-body">
          <div className="dv-no-url">
            <NoUrlIcon />
            <p>No website URL configured</p>
          </div>
        </div>
      </div>
    );
  }
  const href = makeHref(siteUrl);
  return (
    <div className="desktop-view">
      <div className="dv-header">
        <span className="dv-title">{name} — Desktop View</span>
        <a href={href} target="_blank" rel="noopener noreferrer">
          Open in new tab ↗
        </a>
      </div>
      <div className="dv-body">
        <iframe src={href} title={`${name} desktop`} loading="lazy" />
      </div>
    </div>
  );
}

function NoUrlIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  );
}
